import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { CalendarDays, FileText, Layers, UserCircle } from 'lucide-react';
import { useCreateTask } from '../../api/tasks';
import { useProjectList } from '../../api/projects';
import { useEngineerList } from '../../api/engineers';
import { useEpicsByProject } from '../../api/epics';
import { applyServerErrors } from '../../lib/formErrors';
import PageHeader from '../../components/layout/PageHeader';
import Button from '../../components/ui/Button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

const SELECT_CLS = 'h-9 w-full rounded-md border border-input bg-background px-3 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring';

interface FormValues {
  title: string; description: string; points: number; dueDate: string;
  projectId: string; assigneeId: string; type: string; epicId: string;
}

export default function TaskCreatePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const presetProjectId = searchParams.get('projectId') ?? '';

  const { mutate, isPending } = useCreateTask();
  const { data: projects }    = useProjectList();
  const { data: engineers }   = useEngineerList();

  const { register, handleSubmit, setError, watch, setValue, formState: { errors } } = useForm<FormValues>({
    defaultValues: { projectId: presetProjectId },
  });

  const selectedProjectId = watch('projectId');
  const { data: epics } = useEpicsByProject(selectedProjectId);

  useEffect(() => {
    setValue('epicId', '');
  }, [selectedProjectId, setValue]);

  const onSubmit = handleSubmit((values) => {
    mutate(
      {
        ...values,
        points:     Number(values.points),
        description: values.description || undefined,
        assigneeId:  values.assigneeId  || undefined,
        epicId:      values.epicId      || undefined,
      },
      {
        onSuccess: (task) => { toast.success('Task created.'); navigate(`/tasks/${task.id}`); },
        onError:   (e) => applyServerErrors(e, setError),
      },
    );
  });

  return (
    <div className="max-w-xl">
      <PageHeader title="New task" />

      <Card className="overflow-hidden">
        <form onSubmit={onSubmit}>
          {/* Task details */}
          <div className="flex items-center gap-2.5 border-b border-border px-6 py-4">
            <div className="flex h-7 w-7 items-center justify-center rounded-md bg-primary/10">
              <FileText className="h-4 w-4 text-primary" />
            </div>
            <p className="text-sm font-semibold text-foreground">Task details</p>
          </div>
          <div className="space-y-4 px-6 py-5">
            <div className="space-y-1.5">
              <Label>Title <span className="text-destructive">*</span></Label>
              <Input {...register('title', { required: 'Title is required' })} placeholder="Task title" />
              {errors.title && <p className="text-xs text-destructive">{errors.title.message}</p>}
            </div>
            <div className="space-y-1.5">
              <Label>Description</Label>
              <Textarea rows={3} {...register('description')} placeholder="Optional task description" />
            </div>
          </div>

          {/* Scheduling */}
          <div className="flex items-center gap-2.5 border-y border-border px-6 py-4">
            <div className="flex h-7 w-7 items-center justify-center rounded-md bg-violet-50 dark:bg-violet-950/40">
              <CalendarDays className="h-4 w-4 text-violet-600 dark:text-violet-400" />
            </div>
            <p className="text-sm font-semibold text-foreground">Scheduling</p>
          </div>
          <div className="grid grid-cols-1 gap-4 px-6 py-5 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Points <span className="text-destructive">*</span></Label>
              <Input type="number" min={1} {...register('points', { required: 'Points are required', min: { value: 1, message: 'Minimum 1' } })} />
              {errors.points && <p className="text-xs text-destructive">{errors.points.message}</p>}
            </div>
            <div className="space-y-1.5">
              <Label>Due date <span className="text-destructive">*</span></Label>
              <Input type="date" {...register('dueDate', { required: 'Due date is required' })} />
              {errors.dueDate && <p className="text-xs text-destructive">{errors.dueDate.message}</p>}
            </div>
          </div>

          {/* Assignment */}
          <div className="flex items-center gap-2.5 border-y border-border px-6 py-4">
            <div className="flex h-7 w-7 items-center justify-center rounded-md bg-emerald-50 dark:bg-emerald-950/40">
              <UserCircle className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
            </div>
            <p className="text-sm font-semibold text-foreground">Assignment</p>
          </div>
          <div className="space-y-4 px-6 py-5">
            <div className="space-y-1.5">
              <Label>Project <span className="text-destructive">*</span></Label>
              <select {...register('projectId', { required: 'Project is required' })} className={SELECT_CLS}>
                <option value="">Select a project</option>
                {projects?.filter((p) => p.isActive).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
              {errors.projectId && <p className="text-xs text-destructive">{errors.projectId.message}</p>}
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label>Type</Label>
                <select {...register('type')} className={SELECT_CLS} defaultValue="Feature">
                  <option value="Feature">Feature</option>
                  <option value="Bug">Bug</option>
                  <option value="Test">Test</option>
                  <option value="Review">Review</option>
                  <option value="Chore">Chore</option>
                </select>
              </div>
              <div className="space-y-1.5">
                <Label>Assignee</Label>
                <select {...register('assigneeId')} className={SELECT_CLS}>
                  <option value="">Unassigned</option>
                  {engineers?.filter((e) => e.isActive).map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
                </select>
              </div>
            </div>

            {selectedProjectId && (epics?.length ?? 0) > 0 && (
              <div className="space-y-1.5">
                <div className="flex items-center gap-1.5">
                  <Layers className="h-3.5 w-3.5 text-violet-500" />
                  <Label>Epic</Label>
                </div>
                <select {...register('epicId')} className={SELECT_CLS}>
                  <option value="">No epic</option>
                  {epics!.map((e) => <option key={e.id} value={e.id}>{e.title}</option>)}
                </select>
              </div>
            )}
          </div>

          <div className="flex gap-2 border-t border-border bg-muted/30 px-6 py-4">
            <Button type="submit" loading={isPending}>Create task</Button>
            <Button type="button" variant="secondary" onClick={() => navigate(-1)}>Cancel</Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
